import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaLock, FaLockOpen } from 'react-icons/fa';

const ConversionExplanationBinaryToAll = ({ conversionType, fromUnit, toUnit, inputValue, result, darkMode, isExample, isEncodingMode, onEncodingModeToggle }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [abacusValue, setAbacusValue] = useState('');
  const [animatedBinary, setAnimatedBinary] = useState('');
  const [bitShiftStep, setBitShiftStep] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null);
  const [showBase64Table, setShowBase64Table] = useState(false);

  // Reset animated binary and bit shift step when input value changes
  useEffect(() => {
    if (inputValue) {
      setAnimatedBinary(inputValue);
      setBitShiftStep(0);
    }
  }, [inputValue]);

  const handleQuizSubmit = useCallback(() => {
    const isCorrect = quizAnswer.toLowerCase() === result.toLowerCase();
    setQuizResult(isCorrect);
  }, [quizAnswer, result]);


  const getBitShiftingVisualization = useCallback((binary) => {
    const animate = () => {
      if (bitShiftStep < binary.length) {
        setAnimatedBinary(prev => prev.slice(0, bitShiftStep) + '.' + prev.slice(bitShiftStep));
        setBitShiftStep(prev => prev + 1);
      } else {
        setBitShiftStep(0);
        setAnimatedBinary(binary);
      }
    };

    return (
      <div>
        <p>Watch how the binary point shifts:</p>
        <div className="font-mono text-2xl my-4">{animatedBinary || binary}</div>
        <button onClick={animate} className="bg-blue-500 text-white px-4 py-2 rounded">
          Shift Binary Point
        </button>
      </div>
    );
  }, [bitShiftStep, animatedBinary]);

  const renderExampleOrConversionText = useCallback((binaryValue, isExample) => (
    isExample ? (
      <p className="mb-4 text-yellow-600 italic">
        Example: Let's convert the binary number {binaryValue} to {toUnit}.
      </p>
    ) : (
      <p className="mb-4 text-green-600">
        Your conversion: {binaryValue} (Binary) to {toUnit}
      </p>
    )
  ), [toUnit]);

  const getSteps = useCallback((binaryValue) => {
    const decimalValue = parseInt(binaryValue, 2);
    const scientificNotation = decimalValue.toExponential(2);
    const binaryScientificNotation = getBinaryScientificNotation(binaryValue);

    return [
      { title: "Step 1: Two-Step Approach", content: getTwoStepApproach(binaryValue, decimalValue, scientificNotation) },
      { title: "Step 2: Binary Scientific Notation", content: getBinaryScientificNotationStep(binaryValue, binaryScientificNotation) },
      { title: "Step 3: Bit-Shifting Visualization", content: getBitShiftingVisualization(binaryValue) },
      { title: "Step 4: Comparison Method", content: getComparisonMethod(binaryValue, decimalValue, scientificNotation, binaryScientificNotation) },
      { title: "Step 5: Interactive Binary Abacus", content: getInteractiveBinaryAbacus(binaryValue) },
      { title: "Step 6: Binary Exponent Focus", content: getBinaryExponentFocus(binaryValue) },
      { title: "Step 7: Base-2 Logarithm Approach", content: getBase2LogarithmApproach(decimalValue) },
      { title: "Step 8: Pattern Recognition", content: getPatternRecognition(binaryValue) },
      { title: "Step 9: Normalized vs. Denormalized Forms", content: getNormalizedVsDenormalized(binaryValue) },
      { title: "Historical Context", content: getHistoricalContext() },
    ];
  }, [getBitShiftingVisualization]);

  const getBinaryToScientificNotationExplanation = useCallback((binaryValue, isExample) => {
    const decimalValue = parseInt(binaryValue, 2);
    const scientificNotation = decimalValue.toExponential();
    const [coefficient, exponent] = scientificNotation.split('e');
    const adjustedCoefficient = parseFloat(coefficient).toFixed(4);
    const adjustedExponent = parseInt(exponent);
    const binaryScientificNotation = getBinaryScientificNotation(binaryValue);

    const steps = [
      {
        step: 1,
        description: "Convert binary to decimal",
        detail: `Convert ${binaryValue} to decimal: ${decimalValue}`,
        visual: (
          <span className="font-mono">
            <span className="font-bold text-blue-700">{binaryValue}</span> (binary) = 
            <span className="font-bold text-green-700">{decimalValue}</span> (decimal)
          </span>
        )
      },
      {
        step: 2,
        description: "Identify the most significant digit",
        detail: `The most significant digit is ${decimalValue.toString()[0]}`,
        visual: (
          <span className="font-mono">
            <span className="font-bold text-red-700">{decimalValue.toString()[0]}</span>
            {decimalValue.toString().slice(1)}
          </span>
        )
      },
      {
        step: 3,
        description: "Move the decimal point",
        detail: `Move the decimal point ${Math.abs(adjustedExponent)} place${Math.abs(adjustedExponent) !== 1 ? 's' : ''} ${adjustedExponent > 0 ? 'left' : 'right'}`,
        visual: (
          <span className="font-mono">
            {decimalValue.toString()[0]}.
            {decimalValue.toString().slice(1, 5)}
            <span className="text-blue-700 font-bold">
              {adjustedExponent > 0 ? '←'.repeat(Math.abs(adjustedExponent)) : '→'.repeat(Math.abs(adjustedExponent))}
            </span>
            {decimalValue.toString().slice(5)}
          </span>
        )
      },
      {
        step: 4,
        description: "Write the coefficient",
        detail: `The coefficient becomes ${adjustedCoefficient}`,
        visual: (
          <span className="font-mono font-bold text-green-700">{adjustedCoefficient}</span>
        )
      },
      {
        step: 5,
        description: "Determine the exponent",
        detail: `The exponent is ${adjustedExponent}`,
        visual: (
          <span className="font-mono">
            × 10<sup className="font-bold text-purple-700">{adjustedExponent}</sup>
          </span>
        )
      },
      {
        step: 6,
        description: "Combine the parts (Decimal Scientific Notation)",
        detail: `Combine as ${adjustedCoefficient} × 10^${adjustedExponent}`,
        visual: (
          <span className="font-mono text-xl font-bold">
            <span className="text-green-700">{adjustedCoefficient}</span> × 10<sup className="text-purple-700">{adjustedExponent}</sup>
          </span>
        )
      },
      {
        step: 7,
        description: "Binary Scientific Notation",
        detail: `Express the result in binary scientific notation`,
        visual: (
          <span className="font-mono text-xl font-bold">
            <span className="text-green-700">{binaryScientificNotation}</span>
          </span>
        )
      }
    ];

    return (
      <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
        <h3 className="text-2xl font-semibold mb-4">How Binary to Scientific Notation Conversion Works</h3>
        {renderExampleOrConversionText(binaryValue, isExample)}
        
        <div className={`mb-6 p-4 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'} rounded-lg`}>
          <h4 className="font-semibold mb-2">What is Scientific Notation?</h4>
          <p>
            Scientific notation is a way of expressing numbers that are too large or too small 
            to be conveniently written in decimal form. It's written in the form a × 10^n, where 
            1 &le; |a| &lt; and n is an integer. For binary numbers, we can use a similar notation 
            with base 2 instead of 10.
          </p>
        </div>

        <div className="mb-6">
          <h4 className="font-semibold mb-2">Conversion Process:</h4>
          {steps.map((step, index) => (
            <motion.div 
              key={step.step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`mb-4 p-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg`}
            >
              <h5 className="font-semibold">Step {step.step}: {step.description}</h5>
              <p className="mb-2">{step.detail}</p>
              <div className={`mt-2 p-2 ${darkMode ? 'bg-gray-700' : 'bg-white'} rounded text-center`}>
                {step.visual}
              </div>
            </motion.div>
          ))}
        </div>

        <div className={`mb-6 p-4 ${darkMode ? 'bg-green-900' : 'bg-green-100'} rounded-lg`}>
          <h4 className="font-semibold mb-2">Final Result:</h4>
          <div className="text-center">
            <p className={`text-3xl font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
              {adjustedCoefficient} × 10<sup>{adjustedExponent}</sup> (Decimal)
            </p>
            <p className={`text-3xl font-bold mt-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
              {binaryScientificNotation} (Binary)
            </p>
            <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {binaryValue} in binary notation
            </p>
          </div>
        </div>

        {/* Add the "Why is Scientific Notation Important?", "Real-world Applications", and "Quick Tips" sections here */}
        
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getBitShiftingVisualization]);

  const getBinaryToDecimalExplanation = useCallback((binaryValue, isExample) => {
    const decimalValue = parseInt(binaryValue, 2);

    const steps = binaryValue.split('').reverse().map((bit, index) => ({
      bit: parseInt(bit),
      power: index,
      value: parseInt(bit) * Math.pow(2, index)
    }));

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Binary to Decimal Conversion Works</h3>
        {renderExampleOrConversionText(binaryValue, isExample)}
        <p className="mb-4">
          To convert binary to decimal, we multiply each bit by 2 raised to the power of its position (starting from 0) and sum the results.
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Bit</th>
                <th className="px-4 py-2">Position</th>
                <th className="px-4 py-2">Calculation</th>
                <th className="px-4 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <motion.tr 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">
                    <span className={step.bit === 1 ? 'text-green-500 font-bold' : 'text-red-500'}>{step.bit}</span>
                  </td>
                  <td className="border px-4 py-2">{step.power}</td>
                  <td className="border px-4 py-2">{step.bit} × 2<sup>{step.power}</sup></td>
                  <td className="border px-4 py-2">{step.value}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4">
          Sum of all values: <span className="font-bold text-purple-500">{decimalValue}</span> (Decimal)
        </p>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{binaryValue}</span> (Binary) = <span className="text-purple-500">{decimalValue}</span> (Decimal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getBinaryToHexadecimalExplanation = useCallback((binaryValue, isExample) => {
    const hexValue = parseInt(binaryValue, 2).toString(16).toUpperCase();
    const paddedBinary = binaryValue.padStart(Math.ceil(binaryValue.length / 4) * 4, '0');
    
    const steps = [];
    for (let i = 0; i < paddedBinary.length; i += 4) {
      const group = paddedBinary.slice(i, i + 4);
      const decimal = parseInt(group, 2);
      const hex = decimal.toString(16).toUpperCase();
      steps.push({ group, decimal, hex });
    }

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Binary to Hexadecimal Conversion Works</h3>
        {renderExampleOrConversionText(binaryValue, isExample)}
        <p className="mb-4">
          To convert binary to hexadecimal, we group the binary digits into sets of 4 (padding with leading zeros if necessary), then convert each group to its hexadecimal equivalent.
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Binary Group</th>
                <th className="px-4 py-2">Decimal</th>
                <th className="px-4 py-2">Hexadecimal</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <motion.tr 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">
                    <span className="font-mono">
                      {step.group.split('').map((bit, i) => (
                        <span key={i} className={bit === '1' ? 'text-green-500 font-bold' : 'text-red-500'}>{bit}</span>
                      ))}
                    </span>
                  </td>
                  <td className="border px-4 py-2">{step.decimal}</td>
                  <td className="border px-4 py-2">
                    <span className="font-bold text-purple-500">{step.hex}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{binaryValue}</span> (Binary) = <span className="text-purple-500">{hexValue}</span> (Hexadecimal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getBinaryToOctalExplanation = useCallback((binaryValue, isExample) => {
    const octalValue = parseInt(binaryValue, 2).toString(8);
    const paddedBinary = binaryValue.padStart(Math.ceil(binaryValue.length / 3) * 3, '0');
    
    const steps = [];
    for (let i = 0; i < paddedBinary.length; i += 3) {
      const group = paddedBinary.slice(i, i + 3);
      const decimal = parseInt(group, 2);
      const octal = decimal.toString(8);
      steps.push({ group, decimal, octal });
    }

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Binary to Octal Conversion Works</h3>
        {renderExampleOrConversionText(binaryValue, isExample)}
        <p className="mb-4">
          To convert binary to octal, we group the binary digits into sets of 3 (padding with leading zeros if necessary), then convert each group to its octal equivalent.
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Binary Group</th>
                <th className="px-4 py-2">Decimal</th>
                <th className="px-4 py-2">Octal</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <motion.tr 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">
                    <span className="font-mono">
                      {step.group.split('').map((bit, i) => (
                        <span key={i} className={bit === '1' ? 'text-green-500 font-bold' : 'text-red-500'}>{bit}</span>
                      ))}
                    </span>
                  </td>
                  <td className="border px-4 py-2">{step.decimal}</td>
                  <td className="border px-4 py-2">
                    <span className="font-bold text-purple-500">{step.octal}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{binaryValue}</span> (Binary) = <span className="text-purple-500">{octalValue}</span> (Octal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getBinaryToBase36Explanation = useCallback((binaryValue, isExample) => {
    const decimalValue = parseInt(binaryValue, 2);
    const base36Value = decimalValue.toString(36).toUpperCase();
    
    const steps = [];
    let remainingValue = decimalValue;
    let position = 0;

    while (remainingValue > 0) {
      const remainder = remainingValue % 36;
      const base36Digit = remainder.toString(36).toUpperCase();
      steps.push({ position, remainder, base36Digit, value: remainingValue });
      remainingValue = Math.floor(remainingValue / 36);
      position++;
    }

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Binary to Base-36 Conversion Works</h3>
        {renderExampleOrConversionText(binaryValue, isExample)}
        <p className="mb-4">
          To convert binary to base-36, we first convert the binary to decimal, then convert the decimal to base-36.
        </p>
        <p className="mb-4">
          Binary <span className="text-blue-500">{binaryValue}</span> is equal to decimal <span className="text-purple-500">{decimalValue}</span>.
        </p>
        <p className="mb-4">
          Now, let's convert <span className="text-purple-500">{decimalValue}</span> to base-36:
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Step</th>
                <th className="px-4 py-2">Calculation</th>
                <th className="px-4 py-2">Remainder</th>
                <th className="px-4 py-2">Base-36 Digit</th>
              </tr>
            </thead>
            <tbody>
              {steps.reverse().map((step, index) => (
                <motion.tr 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">{index + 1}</td>
                  <td className="border px-4 py-2">{step.value} ÷ 36 = {Math.floor(step.value / 36)} remainder {step.remainder}</td>
                  <td className="border px-4 py-2">{step.remainder}</td>
                  <td className="border px-4 py-2">
                    <span className="font-bold text-indigo-500">{step.base36Digit}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{binaryValue}</span> (Binary) = <span className="text-purple-500">{base36Value}</span> (Base-36)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getBinaryToBase64Explanation = useCallback((binaryValue, isExample) => {
    const decimalValue = parseInt(binaryValue, 2);
    const isPadded = binaryValue.length % 24 !== 0;
    
    const byteArray = [
      (decimalValue >> 16) & 0xFF,
      (decimalValue >> 8) & 0xFF,
      decimalValue & 0xFF
    ].filter(byte => byte !== 0 || isPadded);
    
    const base64Value = btoa(String.fromCharCode.apply(null, byteArray));

    const ConversionStep = ({ step, title, description, content }) => (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: step * 0.1 }}
        className={`mb-6 p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg`}
      >
        <h5 className="font-semibold text-lg mb-2">{`Step ${step}: ${title}`}</h5>
        <p className="mb-2">{description}</p>
        {content}
      </motion.div>
    );

    const Base64EncodingTable = () => (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-6 overflow-hidden"
      >
        <h4 className="text-lg font-semibold mb-2">Base64 Encoding Table:</h4>
        <div className="grid grid-cols-8 gap-2 md:grid-cols-16">
          {'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'.split('').map((char, index) => (
            <div
              key={char}
              className={`p-2 text-center rounded ${index < 26
                ? 'bg-blue-100 text-blue-800'
                : index < 52
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
                } ${darkMode ? 'dark:bg-gray-700 dark:text-white' : ''}`}
            >
              <span className="font-bold">{char}</span>
              <span className="text-xs block">{index}</span>
            </div>
          ))}
        </div>
      </motion.div>
    );

    return (
      <div className={`mt-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-semibold">How Binary to Base-64 Conversion Works</h3>
          <motion.button
            onClick={onEncodingModeToggle}
            className={`flex items-center px-3 py-1 rounded-full ${
              darkMode
                ? isEncodingMode
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-300'
                : isEncodingMode
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-200 text-gray-600'
            } transition-colors duration-200`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isEncodingMode ? (
              <>
                <FaLock className="mr-2" />
                <span className="text-sm">Encoding</span>
              </>
            ) : (
              <>
                <FaLockOpen className="mr-2" />
                <span className="text-sm">Standard</span>
              </>
            )}
          </motion.button>
        </div>

        {renderExampleOrConversionText(binaryValue, isExample)}
        
        <ConversionStep
          step={1}
          title="Group binary digits"
          description="We group the binary digits into sets of 8 bits (1 byte)."
          content={
            <div className="font-mono">
              <div className="flex flex-wrap justify-center items-center space-x-4 mb-4">
                {binaryValue.match(/.{1,8}/g).map((byte, index) => (
                  <div key={index} className="mb-2 text-center">
                    <span className="block px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-purple-600 dark:text-purple-400 font-semibold">
                      {byte.padStart(8, '0')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <ConversionStep
          step={2}
          title="Convert bytes to decimal values"
          description="We convert each byte to its decimal equivalent."
          content={
            <div className="font-mono">
              <div className="flex flex-wrap justify-center items-center space-x-4 mb-4">
                {binaryValue.match(/.{1,8}/g).map((byte, index) => (
                  <div key={index} className="mb-2 text-center">
                    <span className="block px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-t-lg text-purple-600 dark:text-purple-400 font-semibold">
                      {byte.padStart(8, '0')}
                    </span>
                    <span className="block px-3 py-1 bg-blue-100 dark:bg-blue-900 rounded-b-lg text-blue-600 dark:text-blue-400 font-bold">
                      {parseInt(byte, 2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <ConversionStep
          step={3}
          title="Group into 6-bit segments"
          description="We regroup the binary digits into 6-bit segments for Base64 encoding."
          content={
            <div className="font-mono">
              <div className="flex flex-wrap justify-center items-center space-x-4 mb-4">
                {binaryValue.match(/.{1,6}/g).map((segment, index) => (
                  <div key={index} className="mb-2 text-center">
                    <span className="block px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-indigo-600 dark:text-indigo-400 font-semibold">
                      {segment.padEnd(6, '0')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <ConversionStep
          step={4}
          title="Convert to Base64 characters"
          description="We convert each 6-bit segment to its corresponding Base64 character."
          content={
            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="font-semibold">6-bit segments to Base64 characters:</span>
                <button
                  onClick={() => setShowBase64Table(!showBase64Table)}
                  className={`px-4 py-2 rounded ${darkMode
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                    } transition-colors duration-200`}
                >
                  {showBase64Table ? 'Hide Base64 Table' : 'Show Base64 Table'}
                </button>
              </div>
              {showBase64Table && <Base64EncodingTable />}
              <div className="flex flex-wrap justify-center items-center space-x-4 mb-4">
                {binaryValue.match(/.{1,6}/g).map((segment, index) => (
                  <div key={index} className="mb-2 text-center">
                    <span className="block px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-t-lg text-indigo-600 dark:text-indigo-400 font-semibold">
                      {segment.padEnd(6, '0')}
                    </span>
                    <span className="block px-3 py-1 bg-green-100 dark:bg-green-900 rounded-b-lg text-green-600 dark:text-green-400 font-bold">
                      {base64Value[index]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <ConversionStep
          step={5}
          title="Final Base64 encoding"
          description={`The final Base64 encoding of the binary number ${binaryValue}.`}
          content={
            <div className="mt-2 text-center">
              <span className="font-mono font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900 px-4 py-2 rounded inline-block">
                {base64Value}
              </span>
              {base64Value.endsWith('=') && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Note: Padding ('=') is added if the number of bits is not divisible by 6.
                </p>
              )}
            </div>
          }
        />

        <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-900 rounded-lg shadow-md">
          <h4 className="text-xl font-semibold mb-4 text-blue-800 dark:text-blue-200">Important Notes:</h4>
          <ul className="list-disc list-inside space-y-2 text-blue-700 dark:text-blue-300">
            <li>Base64 encoding uses a 64-character alphabet to represent binary data.</li>
            <li>Each Base64 digit represents 6 bits of binary data.</li>
            <li>Padding ('=') is used to ensure the encoded length is a multiple of 4 characters.</li>
            <li>This method ensures consistent results with standard Base64 encoding libraries.</li>
          </ul>
        </div>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, isEncodingMode, onEncodingModeToggle]);

  const getBinaryToRomanExplanation = useCallback((binaryValue, isExample) => {
    const decimalValue = parseInt(binaryValue, 2);
    const romanNumerals = [
      { value: 1000, numeral: 'M' },
      { value: 900, numeral: 'CM' },
      { value: 500, numeral: 'D' },
      { value: 400, numeral: 'CD' },
      { value: 100, numeral: 'C' },
      { value: 90, numeral: 'XC' },
      { value: 50, numeral: 'L' },
      { value: 40, numeral: 'XL' },
      { value: 10, numeral: 'X' },
      { value: 9, numeral: 'IX' },
      { value: 5, numeral: 'V' },
      { value: 4, numeral: 'IV' },
      { value: 1, numeral: 'I' }
    ];

    let remainingValue = decimalValue;
    const steps = [];
    let romanResult = '';

    for (const { value, numeral } of romanNumerals) {
      while (remainingValue >= value) {
        steps.push({ value, numeral, remaining: remainingValue });
        romanResult += numeral;
        remainingValue -= value;
      }
    }

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Binary to Roman Numeral Conversion Works</h3>
        {renderExampleOrConversionText(binaryValue, isExample)}
        <p className="mb-4">
          To convert binary to Roman numerals, we first convert the binary to decimal, then convert the decimal to Roman numerals.
        </p>
        <p className="mb-4">
          Binary <span className="text-blue-500">{binaryValue}</span> is equal to decimal <span className="text-purple-500">{decimalValue}</span>.
        </p>
        <p className="mb-4">
          Now, let's convert <span className="text-purple-500">{decimalValue}</span> to Roman numerals:
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Step</th>
                <th className="px-4 py-2">Remaining Value</th>
                <th className="px-4 py-2">Roman Numeral Added</th>
                <th className="px-4 py-2">Result So Far</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <motion.tr 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">{index + 1}</td>
                  <td className="border px-4 py-2">{step.remaining}</td>
                  <td className="border px-4 py-2">{step.numeral}</td>
                  <td className="border px-4 py-2">
                    <span className="font-bold text-indigo-500">
                      {romanResult.slice(0, romanResult.indexOf(step.numeral) + step.numeral.length)}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{binaryValue}</span> (Binary) = <span className="text-purple-500">{romanResult}</span> (Roman Numeral)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getTwoStepApproach = (binary, decimal, scientific) => (
    <div>
      <p>1. Convert binary to decimal:</p>
      <p className="ml-4">{binary}<sub>2</sub> = {decimal}<sub>10</sub></p>
      <p>2. Convert decimal to scientific notation:</p>
      <p className="ml-4">{decimal}<sub>10</sub> = {scientific}</p>
    </div>
  );

  const getBinaryScientificNotation = (binary) => {
    const firstOneIndex = binary.indexOf('1');
    const mantissa = binary.slice(firstOneIndex, firstOneIndex + 4).padEnd(4, '0');
    const exponent = binary.length - firstOneIndex - 1;
    return `1.${mantissa} × 2^${exponent}`;
  };

  const getBinaryScientificNotationStep = (binary, binaryScientific) => (
    <div>
      <p>Binary scientific notation: {binaryScientific}</p>
      <p>This representation maintains the binary nature of the number.</p>
    </div>
  );

  const getComparisonMethod = (binary, decimal, scientific, binaryScientific) => (
    <table className="w-full border-collapse border border-gray-300">
      <thead>
        <tr className="bg-gray-100">
          <th className="border border-gray-300 p-2">Binary</th>
          <th className="border border-gray-300 p-2">Decimal</th>
          <th className="border border-gray-300 p-2">Scientific (Decimal)</th>
          <th className="border border-gray-300 p-2">Scientific (Binary)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border border-gray-300 p-2">{binary}</td>
          <td className="border border-gray-300 p-2">{decimal}</td>
          <td className="border border-gray-300 p-2">{scientific}</td>
          <td className="border border-gray-300 p-2">{binaryScientific}</td>
        </tr>
      </tbody>
    </table>
  );

  const getInteractiveBinaryAbacus = (binary) => {
    const toggleBit = (index) => {
      const newValue = abacusValue ? abacusValue : binary;
      const toggledBit = newValue[index] === '0' ? '1' : '0';
      const updatedValue = newValue.substr(0, index) + toggledBit + newValue.substr(index + 1);
      setAbacusValue(updatedValue);
    };

    return (
      <div>
        <p>Click on the beads to toggle bits:</p>
        <div className="flex justify-center items-center space-x-2 my-4">
          {(abacusValue || binary).split('').map((bit, index) => (
            <div key={index} className="flex flex-col items-center">
              <button
                onClick={() => toggleBit(index)}
                className={`w-8 h-8 rounded-full ${
                  bit === '1' ? 'bg-blue-500' : 'bg-gray-300'
                } hover:opacity-75 transition-opacity`}
              ></button>
              <div className="mt-1">{2 ** ((abacusValue || binary).length - index - 1)}</div>
            </div>
          ))}
        </div>
        <p>Binary: {abacusValue || binary}</p>
        <p>Decimal: {parseInt(abacusValue || binary, 2)}</p>
      </div>
    );
  };

  const getBinaryExponentFocus = (binary) => {
    const exponent = binary.length - binary.indexOf('1') - 1;
    return (
      <div>
        <p>The exponent in binary scientific notation is determined by the position of the most significant bit (leftmost 1).</p>
        <div className="font-mono text-xl my-4">
          {binary.split('').map((bit, index) => (
            <span key={index} className={index === binary.indexOf('1') ? 'bg-yellow-200' : ''}>
              {bit}
            </span>
          ))}
        </div>
        <p>Exponent: {exponent}</p>
      </div>
    );
  };

  const getBase2LogarithmApproach = (decimal) => (
    <div>
      <p>Using the base-2 logarithm to determine the exponent:</p>
      <p>log<sub>2</sub>({decimal}) ≈ {Math.log2(decimal).toFixed(2)}</p>
      <p>The floor of this value gives us the exponent for scientific notation.</p>
    </div>
  );

  const getPatternRecognition = (binary) => (
    <div>
      <p>Observe the pattern: For binary numbers greater than or equal to 1, the exponent in scientific notation is always one less than the number of digits before the binary point.</p>
      <table className="w-full border-collapse border border-gray-300 mt-4">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">Binary</th>
            <th className="border border-gray-300 p-2">Scientific Notation</th>
            <th className="border border-gray-300 p-2">Exponent</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 p-2">1</td>
            <td className="border border-gray-300 p-2">1.0 × 2<sup>0</sup></td>
            <td className="border border-gray-300 p-2">0</td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2">10</td>
            <td className="border border-gray-300 p-2">1.0 × 2<sup>1</sup></td>
            <td className="border border-gray-300 p-2">1</td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2">100</td>
            <td className="border border-gray-300 p-2">1.0 × 2<sup>2</sup></td>
            <td className="border border-gray-300 p-2">2</td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2">{binary}</td>
            <td className="border border-gray-300 p-2">{getBinaryScientificNotation(binary)}</td>
            <td className="border border-gray-300 p-2">{binary.length - binary.indexOf('1') - 1}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const getNormalizedVsDenormalized = (binary) => (
    <div>
      <p>In binary floating-point representation, we have two forms:</p>
      <ul className="list-disc list-inside mb-4">
        <li>Normalized: 1.fraction × 2<sup>exponent</sup></li>
        <li>Denormalized: 0.fraction × 2<sup>minimum exponent</sup></li>
      </ul>
      <p>Our number {binary} is represented in normalized form: {getBinaryScientificNotation(binary)}</p>
    </div>
  );

  const getHistoricalContext = () => (
    <div>
      <h5 className="font-semibold mb-2">Timeline:</h5>
      <ul className="list-disc list-inside">
        <li>17th century: Development of scientific notation</li>
        <li>1703: Gottfried Leibniz publishes explanation of binary system</li>
        <li>1940s: Claude Shannon applies binary to digital circuit design</li>
        <li>1950s-60s: Widespread adoption of binary in computing</li>
      </ul>
      <p className="mt-4">The combination of binary and scientific notation became crucial with the rise of digital computers, allowing for efficient representation of very large and very small numbers in binary systems.</p>
    </div>
  );
  const getExplanation = useMemo(() => {
    const binaryValue = inputValue || '1010';
    const isExample = !inputValue;
    
    if (toUnit === 'Decimal') {
      return getBinaryToDecimalExplanation(binaryValue, isExample);
    } else if (toUnit === 'Hexadecimal') {
      return getBinaryToHexadecimalExplanation(binaryValue, isExample);
    } else if (toUnit === 'Octal') {
      return getBinaryToOctalExplanation(binaryValue, isExample);
    } else if (toUnit === 'Base-36') {
      return getBinaryToBase36Explanation(binaryValue, isExample);
    } else if (toUnit === 'Base-64') {
      return getBinaryToBase64Explanation(binaryValue, isExample);
    } else if (toUnit === 'Roman Numeral') {
      return getBinaryToRomanExplanation(binaryValue, isExample);
    } else if (toUnit === 'Scientific Notation') {
      return getBinaryToScientificNotationExplanation(binaryValue, isExample);
    }
  }, [inputValue, toUnit, getBinaryToDecimalExplanation, getBinaryToHexadecimalExplanation, getBinaryToOctalExplanation, getBinaryToBase36Explanation, getBinaryToBase64Explanation, getBinaryToRomanExplanation, getBinaryToScientificNotationExplanation]);

  return (
    <div className={`p-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
      {getExplanation}
      <div className="mt-6">
        <button
          onClick={() => setShowQuiz(!showQuiz)}
          className={`px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
        >
          {showQuiz ? 'Hide Quiz' : 'Test Your Knowledge'}
        </button>
      </div>

      {showQuiz && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}
        >
          <h4 className="font-semibold">Quick Quiz:</h4>
          <p>Convert the binary number {inputValue || '1010'} to {toUnit.toLowerCase()}:</p>
          <input
            type="text"
            value={quizAnswer}
            onChange={(e) => setQuizAnswer(e.target.value)}
            className={`mt-2 px-2 py-1 border ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'} rounded-md`}
          />
          <button
            onClick={handleQuizSubmit}
            className={`ml-2 px-4 py-1 ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white rounded-md transition-colors`}
          >
            Submit
          </button>
          {quizResult !== null && (
            <p className={`mt-2 text-lg font-bold ${quizResult ? 'text-green-500' : 'text-red-500'}`}>
              {quizResult ? 'Correct!' : `Try again! The correct answer is ${result}.`}
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default React.memo(ConversionExplanationBinaryToAll);